import { useCallback, useContext, useState } from 'react';
import { X } from 'react-feather';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import styles from './style/SearchInput.module.css';

type SearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  isClearable?: boolean;
  autoFocus?: boolean;
  className?: string;
};

export const SearchInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder = 'Search analyses...',
  isClearable = true,
  autoFocus = false,
  className,
}: SearchInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const theming = useContext(AppThemingContext);
  const enabled = theming?.enabled;
  const primaryColor = enabled ? theming.palette.primary.main : '#2563eb';

  // input handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  }, [onBlur]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && value) {
      e.preventDefault();
      handleClear();
      return;
    }
    onKeyDown?.(e);
  }, [onKeyDown, value, handleClear]);

  return (
    <div
      className={`${styles.container} ${className || ''}`}
      style={{ '--primary-color': primaryColor } as React.CSSProperties}>
      <div className={`${styles.inputWrapper} ${isFocused ? styles.focused : ''}`}>
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={styles.input} />
        {isClearable && value && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            tabIndex={-1}
            aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

SearchInput.displayName = 'SearchInput';
